import { SanitizedHtml } from '../components/SanitizedHtml';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { legalPagesApi, type DatenschutzBewerberPageContent } from '../services/legalPagesApi';
import { fallbackDatenschutzBewerberData } from '../data/legalPagesData';
import './DatenschutzBewerberPage.css';

const DatenschutzBewerberPage: React.FC = () => {
  const location = useLocation();
  const [datenschutzBewerberData, setDatenschutzBewerberData] = useState<DatenschutzBewerberPageContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Zeitüberschreitung beim Laden der Inhalte.')), 10000)
        );
        
        const apiPromise = legalPagesApi.getDatenschutzBewerberContent();
        
        const content = await Promise.race([apiPromise, timeoutPromise]) as DatenschutzBewerberPageContent;
        setDatenschutzBewerberData(content);
        
      } catch (err) {
        setDatenschutzBewerberData(fallbackDatenschutzBewerberData);
        console.error('Datenschutzhinweise für Bewerber konnten nicht geladen werden:', err);
        setError('Die Inhalte konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="datenschutz-bewerber-loading">
        <div className="container">
          <h2 className="datenschutz-bewerber-loading-text">Lade Inhalte...</h2>
        </div>
      </div>
    );
  }

  if (error || !datenschutzBewerberData) {
    return (
      <div className="datenschutz-bewerber-loading">
        <div className="container">
          <h2 className="datenschutz-bewerber-loading-text">Datenschutzerklärung für Bewerber</h2>
          <p>{error || 'Inhalte können nicht geladen werden'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="datenschutz-bewerber-page">
      <div className="container">
        {/* Header */}
        <div className="datenschutz-bewerber-header">
          <h1 className="datenschutz-bewerber-title">{datenschutzBewerberData.title}</h1>
          <p className="datenschutz-bewerber-updated">
            Letzte Aktualisierung: {new Date(datenschutzBewerberData.lastUpdated).toLocaleDateString('de-DE')}
          </p>
        </div>

        <div className="datenschutz-bewerber-card">
          {/* Introduction */}
          <div className="datenschutz-bewerber-intro">
            <div className="datenschutz-bewerber-intro-content">
              <h2 className="datenschutz-bewerber-intro-title">Information für Bewerber</h2>
              <p className="datenschutz-bewerber-intro-text">
                Diese Datenschutzerklärung informiert Sie über die Verarbeitung Ihrer personenbezogenen Daten 
                im Rahmen des Bewerbungsprozesses bei uns. Ihre Privatsphäre ist uns wichtig.
              </p>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="datenschutz-bewerber-toc">
            <h2 className="datenschutz-bewerber-toc-title">Inhaltsverzeichnis</h2>
            <nav className="datenschutz-bewerber-toc-nav">
              {datenschutzBewerberData.sections
                .sort((a, b) => a.order - b.order)
                .map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  className="datenschutz-bewerber-toc-link"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          {/* Sections */}
          <div className="datenschutz-bewerber-content">
            <div className="datenschutz-bewerber-sections">
              {datenschutzBewerberData.sections
                .sort((a, b) => a.order - b.order)
                .map((section, index) => (
                <div key={index} id={`section-${index}`} className="datenschutz-bewerber-section">
                  <h2 className="datenschutz-bewerber-section-title">
                    {section.title}
                  </h2>
                  <SanitizedHtml className="datenschutz-bewerber-section-content" content={section.content} format="markdown" />
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          {datenschutzBewerberData.contactInfo && (
            <div className="datenschutz-bewerber-contact">
              <h2 className="datenschutz-bewerber-contact-title">Kontakt zum Datenschutz</h2>
              
              <div className="datenschutz-bewerber-contact-info">
                <h3 className="datenschutz-bewerber-company-name">{datenschutzBewerberData.contactInfo.company}</h3>
                <div className="datenschutz-bewerber-address">
                  {datenschutzBewerberData.contactInfo.address.map((line, index) => (
                    <span key={index} className="datenschutz-bewerber-address-line">{line}</span>
                  ))}
                </div>
                
                <div className="datenschutz-bewerber-contact-grid">
                  <div className="datenschutz-bewerber-contact-item">
                    <span className="datenschutz-bewerber-contact-label">E-Mail</span>
                    <a href={`mailto:${datenschutzBewerberData.contactInfo.email}`} className="datenschutz-bewerber-contact-link">
                      {datenschutzBewerberData.contactInfo.email}
                    </a>
                  </div>
                  
                  {datenschutzBewerberData.contactInfo.phone && (
                    <div className="datenschutz-bewerber-contact-item">
                      <span className="datenschutz-bewerber-contact-label">Telefon</span>
                      <a href={`tel:${datenschutzBewerberData.contactInfo.phone}`} className="datenschutz-bewerber-contact-link">
                        {datenschutzBewerberData.contactInfo.phone}
                      </a>
                    </div>
                  )}
                </div>
                
                <div className="datenschutz-bewerber-rights">
                  <h4 className="datenschutz-bewerber-rights-title">Ihre Rechte als Bewerber</h4>
                  <p className="datenschutz-bewerber-rights-text">
                    Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung 
                    Ihrer personenbezogenen Daten. Bei Fragen wenden Sie sich gerne an uns.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="datenschutz-bewerber-footer">
          Diese Datenschutzerklärung für Bewerber wurde zuletzt aktualisiert am: {new Date(datenschutzBewerberData.lastUpdated).toLocaleDateString('de-DE')}<br />
          Stand der Generierung: {new Date(datenschutzBewerberData.generatedAt).toLocaleDateString('de-DE')}
        </div>
      </div>
    </div>
  );
};

export default DatenschutzBewerberPage;
